import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import CategoryMenuBar from "../../components/CategoryMenuBar";
import ItemDetailModal from "../../components/ItemDetailModal";
import CartIcon from "../../components/CartIcon";
import PolicyTextWithFeeHighlight from "../../components/PolicyTextWithFeeHighlight";
import { useCart } from "../../hooks/useCart";
import { normalizeStorefrontPolicyText } from "../../utils/transactionPolicyText";
import "./ShopCatalogue.css";
import "../Products.css";

const ShopCatalogue = () => {
  const {
    businessSlug,
    locationSlug,
    shopIdentifier,
    storefrontHandle,
    locationIdentifier,
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();

  const isShopSpecific = !!(businessSlug && locationSlug);
  const isStorefrontLocationMode = !!(storefrontHandle && locationIdentifier);
  // Direct mode: routed via /shop/:shopIdentifier/products (R4/R7 independent sellers)
  const isDirectShopMode = !!(
    shopIdentifier &&
    !businessSlug &&
    !storefrontHandle
  );

  // Core state
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filter / sort state
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("popularity");
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [selectedHeroImage, setSelectedHeroImage] = useState(null);

  // Modal state (packages)
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);

  // ─── helpers ─────────────────────────────────────────────────────────────
  const normalizeCat = useCallback(
    (v) =>
      String(v || "")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_"),
    [],
  );

  const toTitle = useCallback(
    (v) =>
      String(v || "")
        .replace(/[_-]+/g, " ")
        .split(" ")
        .filter(Boolean)
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
        .join(" "),
    [],
  );

  const getCatValues = useCallback((cat) => {
    if (Array.isArray(cat)) return cat.filter(Boolean);
    if (typeof cat === "string" && cat.trim())
      return cat
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    return [];
  }, []);

  const resolveMediaUrl = useCallback((value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";

    if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) {
      return raw;
    }

    const base = String(process.env.REACT_APP_API_BASE_URL || "").replace(
      /\/$/,
      "",
    );

    if (!base) return raw;

    const path = raw.startsWith("/") ? raw : `/${raw}`;
    return `${base}${path}`;
  }, []);

  // ─── derived category list ────────────────────────────────────────────────
  const shopCategories = useMemo(() => {
    if (!products.length) return [];
    const seen = new Map();
    products.forEach((p) => {
      getCatValues(p.category).forEach((raw) => {
        const key = normalizeCat(raw);
        if (key && !seen.has(key)) seen.set(key, raw);
      });
    });
    const configured = new Map(
      (allCategories || []).map((c) => [normalizeCat(c.value), c]),
    );
    return [...seen.entries()]
      .map(([key, raw]) => {
        const cfg = configured.get(key);
        return {
          value: cfg?.value || raw,
          label: cfg?.label || toTitle(raw),
          icon: cfg?.icon || cfg?.emoji || "📦",
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [products, allCategories, getCatValues, normalizeCat, toTitle]);

  const categoryCounts = useMemo(() => {
    const c = { all: 0 };
    products
      .filter((p) => p.stock > 0)
      .forEach((p) => {
        c.all += 1;
        getCatValues(p.category).forEach((raw) => {
          const k = normalizeCat(raw);
          if (k) c[k] = (c[k] || 0) + 1;
        });
      });
    return c;
  }, [products, getCatValues, normalizeCat]);

  // ─── filtered + sorted items ──────────────────────────────────────────────
  const filteredItems = useMemo(() => {
    let items = products
      .filter((p) => p.stock > 0)
      .map((p) => ({ ...p, itemType: "product" }));

    if (selectedCategory !== "all") {
      items = items.filter((p) =>
        getCatValues(p.category).some(
          (v) => normalizeCat(v) === normalizeCat(selectedCategory),
        ),
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          p.description?.toLowerCase().includes(q),
      );
    }

    switch (sortBy) {
      case "price-low":
        items.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case "price-high":
        items.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case "newest":
        items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      default:
        items.sort(
          (a, b) =>
            (b.bookingsCount || b.purchasesCount || 0) -
            (a.bookingsCount || a.purchasesCount || 0),
        );
    }

    return items;
  }, [
    products,
    selectedCategory,
    searchQuery,
    sortBy,
    getCatValues,
    normalizeCat,
  ]);

  // ─── data fetching ────────────────────────────────────────────────────────
  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/product-categories`,
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setAllCategories(d || []))
      .catch(() => setAllCategories([]));
  }, []);

  useEffect(() => {
    if (!isShopSpecific) return;
    fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/business/${businessSlug}/${locationSlug}`,
    )
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then(setShop)
      .catch(() => setError("Shop not found"));
  }, [businessSlug, locationSlug, isShopSpecific]);

  useEffect(() => {
    if (isShopSpecific && !shop?._id) return;
    setLoading(true);
    const base = process.env.REACT_APP_API_BASE_URL;
    let url;
    if (isShopSpecific) {
      url = `${base}/api/v2/products/shop/${shop._id}`;
    } else if (isStorefrontLocationMode) {
      url = `${base}/api/v2/products/shop/${encodeURIComponent(storefrontHandle)}/${encodeURIComponent(locationIdentifier)}`;
    } else if (isDirectShopMode) {
      url = `${base}/api/v2/products/shop/${shopIdentifier}`;
    } else {
      url = `${base}/api/v2/products/featured?limit=100`;
    }

    fetch(url)
      .then((r) => (r.ok ? r.json() : { products: [] }))
      .then((d) => {
        if (
          (isDirectShopMode || isStorefrontLocationMode) &&
          d?.redirect?.to &&
          d.redirect.to !== location.pathname
        ) {
          navigate(d.redirect.to, { replace: true });
          return;
        }

        const loadedProducts = d.products || [];

        if (
          (isShopSpecific || isStorefrontLocationMode) &&
          loadedProducts.length === 0
        ) {
          navigate("/products", {
            replace: true,
            state: {
              storefrontNotice: "This storefront has no published products yet.",
            },
          });
          return;
        }

        setProducts(loadedProducts);
        // In direct shop mode, derive shop header info from first product's shopId
        if ((isDirectShopMode || isStorefrontLocationMode) && loadedProducts.length > 0 && !shop) {
          const firstShopId = loadedProducts[0]?.shopId;
          if (firstShopId && typeof firstShopId === "object") {
            setShop({
              ...firstShopId,
              displayBrandName:
                loadedProducts[0]?.displayBrandName ||
                firstShopId?.displayBrandName ||
                firstShopId?.brandName ||
                firstShopId?.businessName ||
                null,
              brandImage:
                loadedProducts[0]?.displayBrandLogo ||
                firstShopId?.displayBrandLogo ||
                null,
              storefrontHeroImages:
                loadedProducts[0]?.displayHeroImages ||
                firstShopId?.displayHeroImages ||
                firstShopId?.storefrontHeroImages ||
                [],
              displayPolicyTerms:
                loadedProducts[0]?.displayPolicyTerms ||
                firstShopId?.displayPolicyTerms ||
                "",
              displayPolicyRefund:
                loadedProducts[0]?.displayPolicyRefund ||
                firstShopId?.displayPolicyRefund ||
                "",
              displayPolicyCancellation:
                loadedProducts[0]?.displayPolicyCancellation ||
                firstShopId?.displayPolicyCancellation ||
                "",
            });
          }
        }
      })
      .catch(() => setError("Failed to load products"))
      .finally(() => setLoading(false));
  }, [
    shop,
    isShopSpecific,
    isStorefrontLocationMode,
    isDirectShopMode,
    shopIdentifier,
    storefrontHandle,
    locationIdentifier,
    location.pathname,
    navigate,
  ]);

  // ─── handlers ─────────────────────────────────────────────────────────────
  const handleProductClick = useCallback(
    (item) => {
      if (item.itemType === "product") {
        const shopId = item.shopId?._id || item.shopId || shop?._id || "";
        navigate(`/product/${item._id}${shopId ? `?shopId=${shopId}` : ""}`);
      } else {
        setSelectedItem(item);
        setSelectedItemType(item.itemType);
        setIsModalOpen(true);
      }
    },
    [navigate, shop],
  );

  const hasSelectableVariants = useCallback((item) => {
    if (!item || typeof item !== "object") return false;
    if (item.hasVariants === true) return true;
    if (Number(item.variantCount) > 1) return true;
    if (Array.isArray(item.variants) && item.variants.length > 1) return true;
    return false;
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setSelectedItemType(null);
  }, []);

  const handleAddToCart = useCallback(
    async (item, _type, quantity = 1) => {
      try {
        await addToCart(item._id, quantity);
      } catch {}
    },
    [addToCart],
  );

  const handleGlobalCategoryNavigate = useCallback(
    (categoryValue) => {
      if (!categoryValue || categoryValue === "all") {
        navigate("/products");
        return;
      }
      navigate(`/products?category=${encodeURIComponent(categoryValue)}`);
    },
    [navigate],
  );

  // ─── hero data ─────────────────────────────────────────────────────────────
  const heroImages = useMemo(() => {
    const candidates = [
      ...(Array.isArray(shop?.storefrontHeroImages)
        ? shop.storefrontHeroImages
        : []),
      ...(Array.isArray(shop?.displayHeroImages) ? shop.displayHeroImages : []),
      ...(Array.isArray(shop?.business?.storefrontSetupHeroImages)
        ? shop.business.storefrontSetupHeroImages
        : []),
      shop?.business?.storefrontSetupHeroImageDesktop,
    ]
      .map((value) => resolveMediaUrl(value))
      .filter(Boolean);

    return Array.from(new Set(candidates)).slice(0, 3);
  }, [shop, resolveMediaUrl]);

  const heroImage = selectedHeroImage || heroImages[0] || null;

  useEffect(() => {
    if (!heroImages.length) {
      setSelectedHeroImage(null);
      return;
    }

    const randomIndex = Math.floor(Math.random() * heroImages.length);
    setSelectedHeroImage(heroImages[randomIndex]);
  }, [heroImages]);

  const shopLogo =
    resolveMediaUrl(
      shop?.brandImage ||
        shop?.displayBrandLogo ||
        shop?.business?.logo?.url ||
        shop?.businessId?.logo?.url ||
        shop?.logoUrl ||
        shop?.profileImage ||
        shop?.logo,
    ) || null;

  const shopAddress =
    [shop?.city, shop?.state].filter(Boolean).join(", ") || "";
  const shopDisplayName =
    shop?.displayBrandName ||
    shop?.brandName ||
    shop?.businessName ||
    "Shop";

  const shopTypes = Array.isArray(shop?.type)
    ? shop.type.filter(Boolean).join(" • ")
    : shop?.type || "";

  const storefrontPolicyTerms =
    String(
      shop?.displayPolicyTerms || shop?.business?.storefrontPolicyTerms || "",
    ).trim() || "";
  const storefrontPolicyRefund =
    normalizeStorefrontPolicyText(
      String(
        shop?.displayPolicyRefund ||
          shop?.business?.storefrontPolicyRefund ||
          "",
      ).trim(),
    ) || "";
  const storefrontPolicyCancellation =
    normalizeStorefrontPolicyText(
      String(
        shop?.displayPolicyCancellation ||
          shop?.business?.storefrontPolicyCancellation ||
          "",
      ).trim(),
    ) || "";
  const hasStorePolicies =
    Boolean(storefrontPolicyTerms) ||
    Boolean(storefrontPolicyRefund) ||
    Boolean(storefrontPolicyCancellation);

  // ─── early exits ──────────────────────────────────────────────────────────
  if (error && !shop) {
    return (
      <div className="shop-catalogue-error">
        <h2>{error}</h2>
        <button onClick={() => navigate("/")}>Go Home</button>
      </div>
    );
  }

  if (loading && (isShopSpecific || isDirectShopMode || isStorefrontLocationMode) && !shop) {
    return <div className="shop-catalogue-loading">Loading catalogue...</div>;
  }

  const globalPreviewCategories = allCategories.slice(0, 15);

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <div className="shop-catalogue-page">
      <CustomerHeader />

      <CartIcon />

      {/* Mobile-only actions above hero */}
      <div className="sc-mobile-actions-wrap">
        <div className="sc-mobile-actions-row">
          <button
            type="button"
            className="sc-mobile-action-btn"
            onClick={() => navigate("/products")}
          >
            View All Products
          </button>
          <button
            type="button"
            className="sc-mobile-action-btn"
            onClick={() => setIsMobileSearchOpen((v) => !v)}
          >
            {isMobileSearchOpen ? "Close Search" : "Search"}
          </button>
        </div>
        {isMobileSearchOpen && (
          <div className="sc-mobile-search-expand">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── TOP BAR: Category pill bar ── */}
      <div className="sc-topbar-preview">
        <div className="sc-topbar sc-topbar--pills">
          <div className="sc-pills-track">
            <button
              className={`sc-pill${selectedCategory === "all" ? " sc-pill--active" : ""}`}
              onClick={() => handleGlobalCategoryNavigate("all")}
            >
              All Products
            </button>
            {globalPreviewCategories.map((cat) => (
              <button
                key={cat.value}
                className={`sc-pill${selectedCategory === cat.value ? " sc-pill--active" : ""}`}
                onClick={() => handleGlobalCategoryNavigate(cat.value)}
              >
                {cat.icon || cat.emoji ? `${cat.icon || cat.emoji} ` : ""}
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── HERO ── */}
      {(isShopSpecific || isStorefrontLocationMode || (isDirectShopMode && shop)) && (
        <div className="sc-hero-section">
          <div className="sc-hero-image">
            {heroImage ? (
              <img src={heroImage} alt={shopDisplayName || "Shop"} />
            ) : (
              <div className="sc-hero-fallback" />
            )}
          </div>

          {shop && (
            <div className="sc-shop-strip">
              <div className="sc-shop-identity">
                <div className="sc-shop-avatar">
                  {shopLogo ? (
                    <img src={shopLogo} alt={shopDisplayName} />
                  ) : (
                    <span>{shopDisplayName?.[0] || "S"}</span>
                  )}
                </div>
                <div className="sc-shop-info">
                  <h2>{shopDisplayName}</h2>
                  {shopAddress && (
                    <p className="sc-shop-address">{shopAddress}</p>
                  )}
                </div>
              </div>

              {hasStorePolicies && (
                <div className="sc-shop-actions">
                  <button
                    type="button"
                    className="sc-policy-trigger-btn"
                    onClick={() => setIsPolicyModalOpen(true)}
                  >
                    Store Policies
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── PRODUCT DISCOVERY TEMPLATE (mirrors /products exactly) ── */}
      <div className="global-products-page">
        <div className="products-page-layout">
          {/* Sidebar */}
          <CategoryMenuBar
            categories={shopCategories}
            selectedCategory={selectedCategory}
            onCategoryClick={setSelectedCategory}
            categoryCounts={categoryCounts}
            mode="sidebar-only"
          />

          {/* Main content */}
          <div className="products-main-content">
            {/* Controls — identical to /products */}
            <div className="products-controls">
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              {!(isShopSpecific || isStorefrontLocationMode || isDirectShopMode) && (
                <div className="sort-dropdown">
                  <label>Sort by:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="popularity">Popularity</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="newest">Newest</option>
                  </select>
                </div>
              )}
            </div>

            {/* Grid — identical to /products */}
            <div className="products-content">
              {loading ? (
                <div className="products-loading">Loading products...</div>
              ) : filteredItems.length === 0 ? (
                <div className="products-empty">
                  <p>
                    No products found
                    {selectedCategory !== "all" ? " in this category" : ""}.
                  </p>
                  {(selectedCategory !== "all" || searchQuery) && (
                    <button
                      onClick={() => {
                        setSelectedCategory("all");
                        setSearchQuery("");
                      }}
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              ) : (
                <div className="products-grid">
                  {filteredItems.map((item) => (
                    <div
                      key={item._id}
                      className="product-card"
                      onClick={() => handleProductClick(item)}
                    >
                      <div className="product-image">
                        <img
                          src={item.images?.[0] || "/default-product.png"}
                          alt={item.name}
                        />
                      </div>
                      <div className="product-info">
                        <h3>{item.name}</h3>
                        {item.brand && (
                          <p className="product-brand">{item.brand}</p>
                        )}
                        <p className="product-price">
                          ${item.price?.toFixed(2)}
                        </p>
                        <button
                          type="button"
                          className="product-card-add-to-cart sc-product-add-btn"
                          onClick={async (event) => {
                            event.stopPropagation();

                            if (hasSelectableVariants(item)) {
                              handleProductClick(item);
                              return;
                            }

                            const result = await addToCart({
                              _id: item._id,
                              shopId:
                                typeof item.shopId === "object"
                                  ? item.shopId._id
                                  : item.shopId,
                            });
                            if (!result?.success) {
                              window.alert(
                                result?.error || "Failed to add to cart",
                              );
                            }
                          }}
                        >
                          {hasSelectableVariants(item)
                            ? "View Options"
                            : "Add to Cart"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Package modal */}
      {isModalOpen && selectedItem && (
        <ItemDetailModal
          item={selectedItem}
          itemType={selectedItemType}
          onClose={handleCloseModal}
          onAddToCart={handleAddToCart}
        />
      )}

      {isPolicyModalOpen && hasStorePolicies && (
        <div
          className="sc-policy-modal-backdrop"
          role="presentation"
          onClick={() => setIsPolicyModalOpen(false)}
        >
          <div
            className="sc-policy-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Store policies"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sc-policy-modal-header">
              <h3>{shopDisplayName} Policies</h3>
              <button
                type="button"
                className="sc-policy-modal-close"
                onClick={() => setIsPolicyModalOpen(false)}
                aria-label="Close policies"
              >
                x
              </button>
            </div>

            <div className="sc-policy-modal-body">
              {storefrontPolicyCancellation && (
                <section className="sc-policy-panel">
                  <h4>Cancellation Policy</h4>
                  <PolicyTextWithFeeHighlight text={storefrontPolicyCancellation} />
                </section>
              )}
              {storefrontPolicyRefund && (
                <section className="sc-policy-panel">
                  <h4>Refund Policy</h4>
                  <PolicyTextWithFeeHighlight text={storefrontPolicyRefund} />
                </section>
              )}
              {storefrontPolicyTerms && (
                <section className="sc-policy-panel">
                  <h4>Terms and Conditions</h4>
                  <PolicyTextWithFeeHighlight text={storefrontPolicyTerms} />
                </section>
              )}
            </div>
          </div>
        </div>
      )}

      <CustomerFooter />
    </div>
  );
};

export default ShopCatalogue;
